import { useEffect, useRef } from 'react';
import { Center, Gltf } from '@react-three/drei';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useShallow } from 'zustand/react/shallow';
import configService from '../../config/config.service';
import {
  PrintedSideSpecData,
  Section,
  Spec,
  Surface,
  TModel,
  useBundleStore,
} from '../hooks/bundle-store';
import {
  HIDDEN_BACK_SIDE_NAME,
  MATERIAL_S_CODE,
  NOT_SECONDE_SURFACE,
  PACKAGE_S_V_CODES,
  S_BASE,
  S_QTY_CODE,
  SECTION_BODY,
  SIZE_CODE,
  SWAG_PRODUCT_D_N_NAME,
} from '../constants';
import {
  addMaterial,
  applyTextureOnModel,
  removeMaterial,
  update3dSurfaceAndSectionCodes,
  updateImageTextsColor,
} from './helpers';
import { computeBoundingBox } from './camera';
import useThemeStore from '../hooks/use-theme-store';

interface ModelItemProps {
  model: TModel;
  printedSideSpecData: PrintedSideSpecData;
}

export default function ModelItem({ model, printedSideSpecData }: ModelItemProps) {
  const shopCode = useThemeStore((state) => state.themeCode);
  const modelRef = useRef<THREE.Group>(null);
  const isSurfacesLoaded = useRef(false);
  const isSectionsLoaded = useRef(false);
  const isSingleProductUseLessNodesRemoved = useRef(false);

  const {
    surfaceIds,
    isSingle,
    isHumanExist,
    swagProductData,
    updateHumanPosition,
    itemsHaveBacklit,
  } = useBundleStore(
    useShallow((state) => ({
      surfaceIds: state?.bundle?.surfaceIds,
      isSingle: state?.bundle?.isSingle,
      isHumanExist: state?.bundle?.isHumanExist,
      swagProductData: state?.swagProductData,
      updateHumanPosition: state?.updateHumanPosition,
      itemsHaveBacklit: state?.itemsHaveBacklit,
    })),
  );

  const modelRefState = useRef<TModel | null>(null);

  //overwrite the code of model sections and surfaces
  useEffect(() => {
    modelRefState.current = model;

    const handleApplySurfaces = (inputModel: TModel | undefined, isReset = false) => {
      const model = inputModel || modelRefState.current;

      model?.sections.forEach((section) => {
        const specs = section?.product?.specs;
        const sectionCode = isReset ? section?.id : (section?.code as string);

        const node = modelRef?.current?.getObjectByName(sectionCode);
        if (node) {
          const duplicatedNode = node.getObjectByName(sectionCode?.toLowerCase());
          if (duplicatedNode) {
            const qtySpec = specs?.find((s) => s.code === S_QTY_CODE);
            if (qtySpec) {
              const nSVCode = qtySpec?.specValue?.code;
              if (nSVCode === '1') {
                duplicatedNode.visible = false;
                //@ts-ignore
                duplicatedNode.needsUpdate = true;
              }
            }
          }
        }

        applyMTSpec(node, specs);

        const shapeNode = node?.children?.find((child) => child?.name?.includes('aaaa'));
        if (shapeNode) {
          const childNameArray = shapeNode?.children?.[0]?.name?.split('_');

          const shapeSpec = section?.product?.specs?.find((s) =>
            childNameArray?.[1]?.toUpperCase()?.includes(s?.code),
          );

          if (shapeSpec) {
            shapeNode?.children?.[0]?.children?.forEach((child) => {
              if (
                child?.userData?.name?.toUpperCase().startsWith(shapeSpec.specValue?.code + '.')
              ) {
                child.visible = true;
                applyMTSpec(child, specs);
              } else child.visible = false;
            });
          }
        }

        const sizeSpec = specs?.find((s) => s.code === SIZE_CODE);
        if (sizeSpec) {
          sizeSpec?.specsValue?.forEach((sv) => {
            const sectionCode = sv?.section?.code;
            const sectionId = sv?.section?.itemId;
            update3dSurfaceAndSectionCodes({
              sectionCode,
              sectionId,
              surfaces: sv?.section?.surfaces,
              model: modelRef?.current as unknown as THREE.Mesh,
              isSemitransparent: sv?.section?.isSemitransparent,
              surfaceIds,
              specs,
              isSwagProduct: section?.isSwagProduct,
              brandTextures: section?.sizeBrandTextures?.[sv.code] || section?.brandTextures,
              modelId: model?.id,
              isReset,
            });
          });
        } else {
          const sectionId = section?.id;
          update3dSurfaceAndSectionCodes({
            sectionCode,
            sectionId,
            surfaces: section?.surfaces as Surface[],
            model: modelRef?.current as unknown as THREE.Mesh,
            isSemitransparent: section?.isSemitransparent as boolean,
            surfaceIds,
            specs,
            isSwagProduct: section?.isSwagProduct,
            brandTextures: section?.brandTextures,
            modelId: model?.id,
            isReset,
          });
        }
      });
    };

    if (model?.sections && !isSurfacesLoaded.current) {
      isSurfacesLoaded.current = true;
      handleApplySurfaces(model);
    }
    const reset = () => {
      handleApplySurfaces(undefined, true);
    };

    window.addEventListener('resetSurfaces', reset);
    return () => {
      window.removeEventListener('resetSurfaces', reset);
    };
  }, [model?.sections, surfaceIds]);

  //manage printed side spec
  useEffect(() => {
    if (!isSectionsLoaded.current) {
      isSectionsLoaded.current = true;
    } else {
      if (printedSideSpecData?.model === model?.id) {
        const s = model?.sections?.find((s) => s?.id === printedSideSpecData?.section);
        s?.surfaces?.forEach((surface, index) => {
          const id = surface?.id;
          const node = modelRef?.current?.getObjectByName(id);
          if (node) {
            const childName = node?.children?.[0]?.name;
            if (
              (index === 0 || s?.diffSectionDesigns) &&
              childName?.includes(HIDDEN_BACK_SIDE_NAME)
            ) {
              const backNode = node?.children?.[0];
              const isOutAsSingleSide = printedSideSpecData?.isOutAsSingleSide;

              if (printedSideSpecData?.addedSurfaces?.includes(NOT_SECONDE_SURFACE)) {
                (typeof isOutAsSingleSide === 'string'
                  ? [node, backNode]
                  : [isOutAsSingleSide ? node : backNode]
                ).forEach((itemNode) => {
                  addMaterial({
                    node: itemNode,
                    isSemitransparent: s.isSemitransparent as boolean,
                  });
                });
              } else if (printedSideSpecData?.removedSurfaces?.includes(NOT_SECONDE_SURFACE)) {
                addMaterial({
                  node: isOutAsSingleSide ? backNode : node,
                  isSemitransparent: s.isSemitransparent as boolean,
                });

                removeMaterial({
                  node: isOutAsSingleSide ? node : backNode,
                  isSemitransparent: s.isSemitransparent as boolean,
                });
              }
            } else {
              if (!surfaceIds?.includes(id) && s.code !== 'CANOPY') {
                removeMaterial({ node, isSemitransparent: s.isSemitransparent as boolean });
              } else {
                addMaterial({ node, isSemitransparent: s.isSemitransparent as boolean });
              }
            }
          }
        });
      }
    }
  }, [printedSideSpecData]);

  //manage co-branding spec
  useEffect(() => {
    const eventFun = (event: any) => {
      const details = event?.detail;
      if (model?.id === details?.modelId) {
        const node = modelRef?.current?.getObjectByName(details?.surfaceId);
        if (node) {
          const isTextureExist = Boolean(details?.texture);
          //@ts-expect-error
          let texture = isTextureExist ? details?.texture : node?.material?.map;
          applyTextureOnModel({
            isSemitransparent: details?.isSemitransparent,
            isSwagProduct: details?.isSwagProduct,
            isTextureExist: true,
            texture,
            node,
          });

          //@ts-expect-error
          node.needsUpdate = true;
          if (details?.isSizeChange) {
            //@ts-expect-error
            node.userData.material = node.material?.map;
          }
        }
      }
    };

    window.addEventListener('changeSectionBranding', eventFun);
    return () => {
      window.removeEventListener('changeSectionBranding', eventFun);
    };
  }, []);

  //manage Swag products
  useEffect(() => {
    if (!isSectionsLoaded.current) {
      isSectionsLoaded.current = true;
    } else {
      if (swagProductData?.model === model?.id) {
        const s = model?.sections?.find((s) => s?.id === swagProductData?.section);
        if (s?.isSwagProduct) {
          const color = swagProductData?.color;
          if (color) {
            const node = modelRef?.current?.getObjectByName(s?.id);
            const designableNode = node?.children?.find(
              (item) => item?.name === SWAG_PRODUCT_D_N_NAME,
            );
            if (designableNode) {
              const mColor = new THREE.Color(color);
              //@ts-expect-error
              designableNode.material.color = mColor;
              //@ts-expect-error
              designableNode.needsUpdate = true;
            }
          }
          const inkColor = swagProductData.inkColor;
          if (inkColor) {
            const surfaceNode = modelRef?.current?.getObjectByName(s?.surfaces?.[0]?.id as string);
            const texture = surfaceNode?.userData?.texture;
            if (texture) {
              //@ts-expect-error
              updateImageTextsColor(texture.image, inkColor, surfaceNode.material);
            }
          }
        }
      }
    }
  }, [swagProductData]);

  // remove usless sections from model
  useEffect(() => {
    if (!isSingleProductUseLessNodesRemoved.current && model) {
      isSingleProductUseLessNodesRemoved.current = true;
      const id = model?.sections?.[0]?.id;
      const node = modelRef?.current?.getObjectByName(id);
      if (isSingle) {
        node?.parent?.children?.forEach((sNode) => {
          if (sNode?.name != id && sNode?.name != 'aaaa') {
            sNode.visible = false;
            //@ts-expect-error
            sNode.needsUpdate = true;
          }
        });
      } else {
        node?.parent?.children?.forEach((element) => {
          if (
            !element?.name?.includes('_2ND_') &&
            !element?.userData?.name?.includes('MAIN') &&
            !element?.userData?.name?.includes('CANOPY') &&
            element?.name != 'aaaa'
          ) {
            element.visible = false;
            //@ts-expect-error
            element.needsUpdate = true;
          }
        });
      }
    }
  }, [Boolean(model), isSingle]);

  function sendUpdateHumanPosition(id: string, timeout: number) {
    const node = modelRef?.current?.getObjectByName(id);
    if (node) {
      const boundingBox = computeBoundingBox(node as THREE.Group);
      const length = boundingBox.max.z - boundingBox.min.z;
      updateHumanPosition(length, timeout);
    }
  }
  //manage position of the human for single product
  useEffect(() => {
    const isSizeSpecExist = model?.isSizeSpecExist;
    if (isSingle && isHumanExist && isSizeSpecExist) {
      const id = model?.sections?.[0]?.id;
      sendUpdateHumanPosition(id, 0);
      const eventFun = (e: any) => {
        const id = e.detail?.id;
        sendUpdateHumanPosition(id, 50);
      };

      window.addEventListener('changeHumanPosition', eventFun);
      return () => {
        window.removeEventListener('changeHumanPosition', eventFun);
      };
    }
  }, [isSingle, isHumanExist]);

  //manage duplicate section visibility
  useEffect(() => {
    const eventFun = (event: any) => {
      const details = event?.detail;
      const qty = details?.qty;
      if (model?.id === details?.modelId) {
        const sectionNode = modelRef.current?.getObjectByName(details?.sectionId);
        const duplicatedSectionNode = sectionNode?.getObjectByName(
          sectionNode?.userData?.name?.toLowerCase(),
        );

        if (duplicatedSectionNode) {
          duplicatedSectionNode.visible = qty === '1' ? false : true;
          //@ts-ignore
          duplicatedSectionNode.needsUpdate = true;
        }
      }
    };

    window.addEventListener('changeDuplicatedSectionVisible', eventFun);
    return () => {
      window.removeEventListener('changeDuplicatedSectionVisible', eventFun);
    };
  }, []);

  //manage material spec
  useEffect(() => {
    const eventFun = (event: any) => {
      const details = event?.detail;
      const color = details?.color;

      const applyMTSpec = (node: any) => {
        const frame = node?.children?.find((child: any) => child?.name?.includes(SECTION_BODY));
        frame?.material?.color?.set(`#${color}`);
      };

      if (model?.id === details?.modelId) {
        const sectionNode = modelRef?.current?.getObjectByName(details?.sectionId);
        applyMTSpec(sectionNode);

        const shapeNode = sectionNode?.children?.find((child) => child?.name?.includes('aaaa'));
        if (shapeNode) {
          shapeNode?.children?.[0]?.children?.forEach((child) => {
            applyMTSpec(child);
          });
        }
      }
    };

    window.addEventListener('changeMaterial', eventFun);
    return () => {
      window.removeEventListener('changeMaterial', eventFun);
    };
  }, []);

  const haveSections = (model?.sections?.length || 0) > 0;

  //manage shape specs
  useEffect(() => {
    const eventFun = (event: any) => {
      const details = event?.detail;
      if (model?.id === details?.modelId) {
        const specs = details?.specs;
        const specCode = details?.specCode;
        const specValueCode = details?.specValueCode;
        const sectionNode = modelRef?.current?.getObjectByName(details?.sectionId);
        const shapeNode = sectionNode?.children?.find((child) => child?.name?.includes('aaaa'));
        if (shapeNode) {
          const childNameArray = shapeNode?.children?.[0]?.name?.split('_');
          const expectedSpecCode = childNameArray?.[1]?.toUpperCase();

          const setVisible = (specValueCode: string) => {
            shapeNode?.children?.[0]?.children?.forEach((child) => {
              if (child?.userData?.name?.toUpperCase().startsWith(specValueCode + '.')) {
                child.visible = true;

                applyMTSpec(child, specs);
              } else child.visible = false;
              //@ts-expect-error
              shapeNode.needsUpdate = true;
            });
          };

          if (specCode === SIZE_CODE) {
            const shapeSpec = specs?.find((s: { code: string }) =>
              expectedSpecCode?.includes(s?.code),
            );
            if (shapeSpec) {
              setVisible(shapeSpec?.specValue?.code);
            }
            return;
          }

          if (expectedSpecCode?.includes(specCode)) {
            setVisible(specValueCode);
          }
        }
      }
    };
    if (haveSections) {
      window.addEventListener('changeShapeSpec', eventFun);
      return () => {
        window.removeEventListener('changeShapeSpec', eventFun);
      };
    }
  }, [haveSections]);

  //manage backlit spec
  useEffect(() => {
    const eventFun = (event: any) => {
      const details = event?.detail;
      const lightBox = details?.lightBox;
      if (details?.sectionId) {
        if (details?.modelId === model?.id) {
          model?.sections?.forEach((section) => {
            const sectionNode = modelRef.current?.getObjectByName(section?.id);
            if (section?.id === details?.sectionId && sectionNode) {
              if (!details?.isBacklit) {
                removeSectionBacklit({ section, sectionNode });
              } else {
                addSectionBacklit({ section, sectionNode, lightBox: true });
              }
            }
          });
        }
      } else {
        model?.sections?.forEach((section) => {
          const sectionNode = modelRef.current?.getObjectByName(section?.id);
          if (section?.isBacklit) {
            if (sectionNode) {
              addSectionBacklit({ section, sectionNode, lightBox });
            }
          } else {
            if (section?.hasBacklitSpec) {
              removeSectionBacklit({ section, sectionNode });
            }
          }
        });
      }
    };

    window.addEventListener('changeBacklit', eventFun);
    return () => {
      window.removeEventListener('changeBacklit', eventFun);
    };
  }, [itemsHaveBacklit]);

  useFrame(() => {
    const shapesNode = modelRef?.current?.children[0]?.children?.[0];
    const isShapeSizeSpecExist = shapesNode?.name === 'aaaa';
    let haveShapeSpec = false;
    let shapeElement: any;
    if (isShapeSizeSpecExist) {
      shapeElement = modelRef?.current?.getObjectByName('aaaa');
    }

    model?.sections?.forEach((section) => {
      const sectionNode = modelRef?.current?.getObjectByName(section?.id);
      // manage size spec
      const sizeSpec = section?.product?.specs?.find((s) => s.code === SIZE_CODE);
      if (sizeSpec) {
        sizeSpec?.specsValue?.forEach((sv) => {
          const node = modelRef?.current?.getObjectByName(sv?.section?.itemId);
          if (node) {
            if (sv?.section?.itemId === sizeSpec?.specValue?.section?.itemId) {
              node.visible = section?.visible;
            } else {
              node.visible = false;
            }
            //@ts-expect-error
            node.needsUpdate = true;
          }
        });
      }

      //manage shape size specs
      if (isShapeSizeSpecExist) {
        const childNameArray = shapesNode?.children?.[0]?.name?.split('_');
        const shapeSizeSpec = section?.product?.specs?.find(
          (s) => s?.code === childNameArray?.[1]?.toUpperCase(),
        );

        if (shapeSizeSpec) {
          haveShapeSpec = true;
          shapeSizeSpec?.specsValue?.forEach((sv) => {
            const shapeNode = shapesNode?.getObjectByName(sv?.code.toLowerCase());

            if (sv?.code === PACKAGE_S_V_CODES[1]) {
              const mtSpec = section?.product?.specs?.find((s) => s?.code === MATERIAL_S_CODE);
              if (mtSpec) {
                const color = mtSpec?.specValue?.code;
                const frame = shapeNode?.children?.find((child) =>
                  child?.name?.includes(SECTION_BODY),
                );
                //@ts-expect-error
                frame?.material?.color?.set(`#${color}`);
              }
            }
            if (shapeNode) {
              if (sv?.code === shapeSizeSpec?.specValue?.code) shapeNode.visible = section?.visible;
              else shapeNode.visible = false;
              //@ts-expect-error
              shapeNode.needsUpdate = true;
            }
          });
        } else {
          if (isSingle && shapeElement) {
            shapeElement.visible = false;
            shapeElement.needsUpdate = true;
          }
        }
      }

      if (sectionNode) {
        sectionNode.visible = section?.visible;
        //@ts-expect-error
        sectionNode.needsUpdate = true;

        //   //backlit
        //   const surNode = sectionNode.getObjectByName(activeSurface);
        //   const material = surNode && surNode?.material;
        //   if (material) {
        //     if (activeSection === section?.id) {
        //       if (lightBox && isBacklitable) {
        //         material.color = new THREE.Color(1, 1, 1);
        //         material.color.multiplyScalar(1.2);
        //         material.metalness = -0.3;
        //       } else {
        //         material.color = new THREE.Color(1, 1, 1);
        //         material.metalness = 0;
        //       }
        //     } else {
        //       material.color = new THREE.Color(1, 1, 1);
        //       material.metalness = 0;
        //     }
        //     surNode.needsUpdate = true;
        //   }
      }
    });
    if (!isSingle && shapeElement && !haveShapeSpec) {
      shapeElement.visible = false;
      shapeElement.needsUpdate = true;
    }
  });

  const x = model?.location?.x ?? 0;
  const z = model?.location?.z ?? 0;
  const y = model?.location?.y ?? 0;
  const rotation = model?.location?.rotation ?? 0;

  return (
    <Center
      top={true}
      position={model?.code?.includes('FLOORING') ? 0 : [x, y, z]}
      rotation={[0, rotation, 0]}>
      <Gltf
        ref={modelRef}
        src={configService.getBaseUrl(shopCode).bucketStorage + '/3D_OBJECT/' + model?.src}
        castShadow
        receiveShadow
      />
    </Center>
  );
}

//backlit functions
const resetBacklit = (material: THREE.MeshStandardMaterial) => {
  if (material.metalness === -0.3) {
    material.color = new THREE.Color(1, 1, 1);
    material.metalness = 0;
  }
};

const removeSectionBacklit = ({ section, sectionNode }: { section: Section; sectionNode: any }) => {
  section?.surfaces?.forEach((surface: Surface) => {
    const surNode = sectionNode.getObjectByName(surface?.id);
    const material = surNode && surNode?.material;
    if (material) {
      resetBacklit(material);
      surNode.needsUpdate = true;
    }
  });
};

const addSectionBacklit = ({
  section,
  sectionNode,
  lightBox,
}: {
  section: Section;
  lightBox: boolean;
  sectionNode: any;
}) => {
  section?.surfaces?.forEach((surface: Surface) => {
    const surNode = sectionNode.getObjectByName(surface?.id);
    const material = surNode && surNode?.material;

    if (material) {
      if (lightBox) {
        material.color = new THREE.Color(1, 1, 1);
        material.color.multiplyScalar(1.2);
        material.metalness = -0.3;
      } else {
        resetBacklit(material);
      }
      surNode.needsUpdate = true;
    }
  });
};

const applyMTSpec = (node: any, specs: any) => {
  const mtSpec = specs?.find((s: Spec) => s?.code === MATERIAL_S_CODE);
  if (mtSpec) {
    const color = mtSpec?.specValue?.code;
    const frame = node?.children?.find((child: any) => child?.name?.includes(SECTION_BODY));
    frame?.material?.color?.set(`#${color}`);
  }
};
